import { useEffect, useState } from "react";

import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { useAdminTracks } from "../use-admin-content";
import { useAdminCommand } from "../use-admin-command";
import { useAttendeeRecords } from "../use-attendee-records";

import { AttendeeFinder } from "./AttendeeFinder";
import { attendeeSpec } from "./content-specs";
import { EntityCrudSection } from "./EntityCrudSection";

const NO_TRACK = "__none__";

/**
 * The attendee list: full CRUD, plus track assignment.
 *
 * Update and delete run through EntityCrudSection as direct writes like every
 * other collection. Create does not: an attendee's document id is sha256 of
 * their email, the same id registration and the importer derive, and a record
 * at any other id is a second copy of that person that no later guard can
 * see. Rules cannot compute a hash, so the id is derived by a trigger and the
 * spec's `createCommand` sends ADD_EVENT_ATTENDEE instead.
 *
 * Track assignment cannot be a form field either. `trackId` appears in no
 * update key list — assigning a track has to update the attendee and their
 * linked membership together — so the dropdown submits ASSIGN_ATTENDEE_TRACKS
 * and waits for the trigger. It rides along as the spec's `rowAction`.
 *
 * Restoring a withdrawn attendee is a command for the same reason. Withdrawal
 * deletes the membership document that puts the event on the person's
 * dashboard, so writing the status back alone leaves a roster row the app
 * shows nothing to; RESTORE_EVENT_ATTENDEE moves both together.
 */
export function AttendeesSection({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const attendees = useAttendeeRecords(eventId);
  const tracks = useAdminTracks(eventId);
  const command = useAdminCommand();

  const busy =
    command.state.status === "submitting" ||
    command.state.status === "watching";
  const trackOptions = tracks.status === "success" ? tracks.data : [];

  // Reload when the TRIGGER has applied the command, not when submit() resolves.
  // submit() returns as soon as the command document is written, which is
  // before the track exists — reloading then shows the old value and looks
  // like the assignment was ignored.
  useEffect(() => {
    if (command.state.status === "succeeded") attendees.reload();
  }, [command.state.status, attendees.reload]);

  return (
    <EntityCrudSection
      eventId={eventId}
      uid={uid}
      items={attendees.status === "success" ? attendees.data : []}
      loading={attendees.status === "loading"}
      error={attendees.status === "error" ? attendees.error : undefined}
      reload={attendees.reload}
      spec={{
        ...attendeeSpec,
        // Search for somebody already on the app instead of typing the address
        // blind. Choosing a person fills the form; saving is still
        // ADD_EVENT_ATTENDEE, so linking and admission are unchanged.
        createFinder: ({ eventId: finderEvent, uid: finderUid, ...props }) => (
          <AttendeeFinder eventId={finderEvent} uid={finderUid} {...props} />
        ),
        bulkAction: (visible) => (
          <div className="space-y-2">
            {/* Bound to the track and restore commands, which share one
                useAdminCommand. EntityCrudSection renders its own feedback for
                the delete command, a different instance that never sees these
                fail. */}
            <AdminCommandFeedback
              state={command.state}
              onReset={command.reset}
              testId="attendee-command"
            />
            <BulkTrackBar
              count={visible.length}
              disabled={busy || trackOptions.length === 0}
              tracks={trackOptions}
              onApply={(trackId) =>
                void command.submit("ASSIGN_ATTENDEE_TRACKS", eventId, uid, {
                  assignments: visible.map((attendee) => ({
                    attendeeId: attendee.id,
                    trackId: trackId === NO_TRACK ? null : trackId,
                  })),
                })
              }
            />
          </div>
        ),
        rowAction: (attendee) => (
          <div className="flex shrink-0 items-center gap-2 max-sm:min-w-0 max-sm:flex-1">
            {attendee.status === "withdrawn" && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                aria-label={`Restore ${attendee.name}`}
                data-testid={`restore-attendee-${attendee.id}`}
                onClick={() =>
                  void command.submit("RESTORE_EVENT_ATTENDEE", eventId, uid, {
                    attendeeId: attendee.id,
                  })
                }
              >
                Restore
              </Button>
            )}
            <Select
              value={attendee.trackId ?? NO_TRACK}
              disabled={busy || trackOptions.length === 0}
              onValueChange={(next) =>
                void command.submit("ASSIGN_ATTENDEE_TRACKS", eventId, uid, {
                  assignments: [
                    {
                      attendeeId: attendee.id,
                      // The service wants an explicit null to clear it;
                      // omitting the key is an invalid field set.
                      trackId: next === NO_TRACK ? null : next,
                    },
                  ],
                })
              }
            >
              <SelectTrigger
                className="w-40 shrink-0 max-sm:w-auto max-sm:min-w-0 max-sm:flex-1"
                testId={`attendee-track-${attendee.id}`}
              >
                <SelectValue placeholder="No track" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TRACK}>No track</SelectItem>
                {trackOptions.map((track) => (
                  <SelectItem key={track.id} value={track.id}>
                    {track.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ),
      }}
    />
  );
}

/**
 * Set one track across every attendee currently shown.
 *
 * The command has always accepted up to 100 assignments; the UI sent exactly
 * one, which made assigning a track after a 200-row import a 200-click job.
 *
 * It applies to the FILTERED set rather than a checkbox selection, because the
 * useful shape after an import is "everyone from this company" or "everyone
 * matching this search" — and a filter is a much cheaper way to say that than
 * ticking rows.
 *
 * Above 100 it refuses rather than splitting into several commands. Chunking
 * would turn one refusal into a partial application spread over several command
 * documents, which is far harder to explain than "narrow the search".
 */
function BulkTrackBar({
  count,
  disabled,
  tracks,
  onApply,
}: {
  count: number;
  disabled: boolean;
  tracks: { id: string; name: string }[];
  onApply: (trackId: string) => void;
}) {
  const [trackId, setTrackId] = useState("");
  const tooMany = count > 100;

  if (count === 0 || tracks.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted px-3 py-2"
      data-testid="attendee-bulk-track"
    >
      <span className="text-sm text-muted-foreground">
        Set track for all {count} shown
      </span>
      <Select value={trackId} onValueChange={setTrackId} disabled={disabled}>
        <SelectTrigger className="w-44" testId="attendee-bulk-track-select">
          <SelectValue placeholder="Choose a track" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_TRACK}>No track</SelectItem>
          {tracks.map((track) => (
            <SelectItem key={track.id} value={track.id}>
              {track.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        size="sm"
        variant="secondary"
        disabled={disabled || !trackId || tooMany}
        data-testid="attendee-bulk-track-apply"
        onClick={() => onApply(trackId)}
      >
        Apply
      </Button>
      {tooMany && (
        <span className="text-xs text-muted-foreground">
          The command takes 100 at a time — narrow the search.
        </span>
      )}
    </div>
  );
}
